// 原系統「靜態內容頁」的 React 版本（display 頁型用）。
//
// 這一類頁面沒有資料來源：內容寫死在 JSP 裡（目前 2 頁，都是教育訓練影片索引）。
// 因此它不吃 useList 的 loading／empty／error 狀態——沒有資料可以載入、可以空、可以出錯。
//
// 結構對齊的依據（scripts/normalize-dom.ts）：
//   - 區塊標題用 <div> 包一層文字。R6「無語意 wrapper 剝除」在預設 unwrap:'any' 下，
//     連「只裝著文字的無屬性 div」也剝（normalize-dom.ts:1250-1251，
//     :1250 是 wrapper 判準、:1251 `if (opts.unwrap === 'any') return children;`），
//     而 className 不在 R3 的
//     屬性 allowlist，所以這個 div 會消失、文字併入父層——與原系統
//     `<div class="form-group">…文字…</div>` 剝完的形狀相同。
//   - <iframe> 只有 src 進比對：width／height／sandbox 都不在 R3 的 allowlist。
//     width／height 照抄原系統（800×500）是為了外觀，不影響 structure。
//   - 刻意**不加** iframe 的 title 屬性：title 在 allowlist 裡，原系統沒有，
//     加了會變成一筆「屬性不同」。這是已知的無障礙取捨，記在報告裡。

import type { ReactElement } from 'react';
import type { LegacyContentItem } from '@/mocks';
import { toUiKey } from './shared';

export interface LegacyStaticContentProps {
  pageId: string;
  items: readonly LegacyContentItem[];
}

/** 原系統 iframe 的尺寸，照抄（width/height 不進 structure 比對，只影響外觀）。 */
const EMBED_WIDTH = 800;
const EMBED_HEIGHT = 500;

export function LegacyStaticContent({ pageId, items }: LegacyStaticContentProps): ReactElement {
  return (
    <div data-ui-id={`${pageId}-content-static`} className="flex flex-col gap-2">
      {items.map((item, index) => {
        const key = `${item.kind}-${String(index)}`;
        switch (item.kind) {
          case 'rule':
            return <hr key={key} className="my-2 border-border" />;
          case 'text':
            return (
              <div
                key={key}
                data-ui-id={`${pageId}-content-heading-${String(index)}`}
                className="font-bold text-ink"
              >
                {item.text}
              </div>
            );
          case 'label': {
            const labelKey = item.text === '' ? String(index) : toUiKey(item.text);
            // 原系統這裡就是「沒有綁控制項的 <label>」：影片標題被寫成 label，
            // 實測 legacy raw-dom 的 educationTraining.jsp 7 個 <label> 都沒有 for 屬性、
            // 也沒有包住任何控制項。改成 <div>／<span> 會讓標籤名對不上（多一筆差異），
            // 補 htmlFor 則指不到任何東西。這是原系統的既有標記缺陷，依計畫只記錄不照修。
            return (
              // biome-ignore lint/a11y/noLabelWithoutControl: 見上方說明，照抄原系統的裸 <label>
              <label
                key={key}
                data-ui-id={`${pageId}-content-label-${labelKey}`}
                className="mb-[5px] inline-block max-w-full font-bold text-ink"
              >
                {item.text}
              </label>
            );
          }
          case 'embed':
            // title 在 normalize-dom 的屬性 allowlist 內（scripts/normalize-dom.ts:298-330
            // 的 'title'），而原系統的 <iframe> 沒有 title：實測 legacy raw-dom 的
            // home/training-education-training.html，7 個 <iframe> 只有
            // src / width / height / sandbox 四個屬性。補上 title 就是 7 筆「屬性不同」。
            // 這是已知的無障礙取捨，寫在報告 C2 裡，不靜默。
            return (
              // biome-ignore lint/a11y/useIframeTitle: 見上方說明，原系統的 iframe 沒有 title
              <iframe
                key={key}
                data-ui-id={`${pageId}-content-embed-${String(index)}`}
                src={item.src}
                width={EMBED_WIDTH}
                height={EMBED_HEIGHT}
                sandbox="allow-scripts allow-same-origin"
                className="max-w-full"
              />
            );
          // 上面四個 case 已窮舉 LegacyContentItem['kind']（TypeScript 保證），
          // 這條只是讓 callback 在型別以外的路徑也一定有回傳值（biome
          // lint/suspicious/useIterableCallbackReturn）。
          default:
            return null;
        }
      })}
    </div>
  );
}
